import { CardStyle, type GameId } from "modules/common/model/models";
import {
  GAME_REGISTRY,
  getDeckGameAdapter,
  getGameMetadata,
  registeredDeckGameIds,
} from "modules/common/gameRegistry";
import { CSSProperties } from "react";
import { ManaColorEnum } from "modules/cards/model/card";
import { TradeListType } from "modules/tradeLists/model/tradeList";

export const BRAND_NAME = "Lootstack";
export const BRAND_TAGLINE = "Your Ultimate TCG Trading Hub";
export const DEFAULT_CURRENCY = "EUR";

export function getGameDisplayName(game: GameId): string {
  return getGameMetadata(game).name;
}
const SEO_GAME_METADATA = Object.values(GAME_REGISTRY).filter(
  (game) => !game.hidden
);

function formatGameList(names: string[], conjunction: string): string {
  if (names.length <= 1) return names[0] ?? "trading card games";
  if (names.length === 2) return `${names[0]} ${conjunction} ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, ${conjunction} ${names.at(-1)}`;
}

/**
 * Selectable (non-hidden) registry games for aggregate-page copy. Individual
 * game pages still carry the full game-specific search intent.
 */
export const SEO_GAME_EXAMPLES = formatGameList(
  SEO_GAME_METADATA.map((game) => game.shortName),
  "and"
);

export const SEO_GAME_KEYWORDS = [
  ...new Set(
    SEO_GAME_METADATA.flatMap((game) => [
      game.name.toLowerCase(),
      game.shortName.toLowerCase(),
    ])
  ),
];

export const IS_PRODUCTION = process.env.NODE_ENV === "production";

export const DeckLocationNameMap = {
  0: "Mainboard",
  1: "Sideboard",
  2: "Companion",
  3: "Commander",
  4: "Considering",
  5: "DON!!",
};
export const TradeListTypeNameMap = {
  1: "Wants",
  2: "Haves",
};
export function formatsForGame(game: GameId): readonly string[] {
  return getDeckGameAdapter(game).formats;
}

// Compatibility view for the few static consumers that need a serializable
// map. It is derived from the registry instead of defining game behavior.
export const Formats: Record<string, readonly string[]> = Object.fromEntries(
  registeredDeckGameIds().map((game) => [game, formatsForGame(game)])
);

// Single-format games render the same list at two URLs; the format page wins
// (it's what deck breadcrumbs link to) and the bare game page canonicalises here.
export function canonicalDeckBrowsePath(game: GameId): string {
  const formats = formatsForGame(game);
  return formats.length === 1
    ? `/decks/${game}/${formats[0]}`
    : `/decks/${game}`;
}

export function formatLabel(game: GameId, slug: string): string {
  return getDeckGameAdapter(game).formatLabel(slug);
}

export enum RaritiesEnum {
  Common = "common",
  Uncommon = "uncommon",
  Rare = "rare",
  Mythic = "mythic",
  Special = "special",
}

export enum CardTypesEnum {
  Artifact = "artifact",
  Creature = "creature",
  Enchantment = "enchantment",
  Instant = "instant",
  Land = "land",
  Planeswalker = "planeswalker",
  Sorcery = "sorcery",
}

export const Rarities = Object.values(RaritiesEnum);

export const TradeListTypesListDropDown = [
  { value: TradeListType.WANTS, label: "Wants" },
  { value: TradeListType.TRADES, label: "Haves" },
];
export const ManaColorNameMap = {
  W: "White",
  G: "Green",
  B: "Black",
  U: "Blue",
  R: "Red",
  D: "Devoid",
  C: "Colorless",
};

export const DisplayNameMap: Record<number, string> = {
  0: "Table",
  1: "Visual Stacks",
  2: "Visual Spoiler",
  3: "Visual Grid",
  4: "Split",
};

export const ManaColorChartColorMapping = () => {
  const colorMapping = new Map<ManaColorEnum, string>();
  colorMapping.set(ManaColorEnum.White, "#E8E6B4");
  colorMapping.set(ManaColorEnum.Black, "#291D2C");
  colorMapping.set(ManaColorEnum.Green, "#62A64F");
  colorMapping.set(ManaColorEnum.Red, "#D23C30");
  colorMapping.set(ManaColorEnum.Blue, "#3BB8E3");
  colorMapping.set(ManaColorEnum.Devoid, "#b0b0b0");
  colorMapping.set(ManaColorEnum.Colorless, "#BEB9B2");
  return colorMapping;
};

export const DeckChartShape = {
  backgroundColor: "none",
  boxShadow: "none",
  width: "auto",
  // p: 1,
  flex: "0 0 auto",
  textAlign: "center",
  overflow: "visible",
  // borderRadius: "50%",
} satisfies CSSProperties;

// Card Stack Heights
export const cardStackHeights = {
  cardHeight: 336,
  overlap: 300,
  headingHeight: 40,
};

export const cardLineActionStyles = {
  button: {
    width: 24,
    height: 24,
    color: "secondary.main",
  },
  icon: {
    width: 20,
    height: 20,
  },
  iconColor: {
    color: "black",
  },
  menuText: {
    fontSize: 14,
  },
  modalRoot: {
    background: "rgba(212,18,18,0.92)",
  },
  menuItem: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
};

export const DeckBarChartStyles: CardStyle = {
  card: DeckChartShape,
  chart: {
    height: 250,
    width: 250,
    marginLeft: "auto",
    marginRight: "auto",
  },
};
